import { getCountableFormatter, getPercentageFormatter } from "../util";
import { ParameterClassification } from "./classification-model";
import { HerdValueModel } from "./cow-herd-value.model";
import { ParameterRange } from "./range.model";

export function isHerdScenario(o: unknown): o is HerdScenario {
  return "cowMortalityFraction" in (o as HerdScenario);
}

export type HerdScenario = {
  cowMortalityFraction: number;
  cowsPregnantFraction: number;
  cowsWithTwinsFraction: number;
  abortionFraction: number;
  stillbirthFraction: number;
  firstWeekLossesFraction: number;
  lossesAfterFirstWeekFraction: number;
  calfGrowthRateKgPerDay: number;
};

export function isHerdScenarioValid(scenario: HerdScenario) {
  return Object.entries(HerdScenarioRange).every(([key, range]) => {
    const typedKey = key as keyof HerdScenario;
    return (
      key in scenario &&
      scenario[typedKey] >= range.min &&
      scenario[typedKey] <= range.max
    );
  });
}

export type HerdScenarioRangeType = {
  [key in keyof HerdScenario]: ParameterRange;
};

const cowCountFormatter = getCountableFormatter("cow", "cows");
const calfCountFormatter = getCountableFormatter("calf", "calves");

export const HerdScenarioRange: Readonly<HerdScenarioRangeType> = {
  cowMortalityFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.05,
    step: 0.005,
    direction: "ltr",
    title: "Annual cow mortality",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: HerdValueModel) => (v: number) =>
      v / valueModel.breedingMotherInitialHeadCount,
    getReverseTransform: (valueModel: HerdValueModel) => (v: number) =>
      Math.round(v * valueModel.breedingMotherInitialHeadCount),
  },
  cowsPregnantFraction: {
    unit: "Hd.",
    min: 0.5,
    max: 1,
    step: 0.01,
    direction: "ltr",
    title: "Cows pregnant",
    toolTip:
      "The total number of cows pregnant (with either a single calf or twins)",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: HerdValueModel) => (v: number) =>
      v / valueModel.cowsAliveAtCalving,
    getReverseTransform: (valueModel: HerdValueModel) => (v: number) =>
      Math.round(v * valueModel.cowsAliveAtCalving),
  },
  cowsWithTwinsFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.1,
    step: 0.005,
    direction: "ltr",
    title: "Cows with twins",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: HerdValueModel) => (v: number) =>
      v / valueModel.cowsPregnant,
    getReverseTransform: (valueModel: HerdValueModel) => (v: number) =>
      Math.round(v * valueModel.cowsPregnant),
  },
  abortionFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.05,
    step: 0.005,
    direction: "ltr",
    title: "Calf Abortions",
    toolTip: "Calves lost through abortion",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: HerdValueModel) => (v: number) =>
      v / valueModel.calvesAliveAtCalving,
    getReverseTransform: (valueModel: HerdValueModel) => (v: number) =>
      Math.round(v * valueModel.calvesAliveAtCalving),
  },
  stillbirthFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.15,
    step: 0.01,
    direction: "ltr",
    title: "Stillborn calves",
    toolTip: "Calves stillborn at full term (does not include abortions)",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: HerdValueModel) => (v: number) =>
      v / valueModel.calvesRemainingAfterAbortions,
    getReverseTransform: (valueModel: HerdValueModel) => (v: number) =>
      Math.round(v * valueModel.calvesRemainingAfterAbortions),
  },
  firstWeekLossesFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.15,
    step: 0.01,
    direction: "ltr",
    title: "First week losses",
    toolTip: "Calves lost in their first week",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: HerdValueModel) => (v: number) =>
      v / (valueModel.calvesRemainingAfterAbortions - valueModel.stillborns),
    getReverseTransform: (valueModel: HerdValueModel) => (v: number) =>
      Math.round(
        v * (valueModel.calvesRemainingAfterAbortions - valueModel.stillborns)
      ),
  },
  lossesAfterFirstWeekFraction: {
    unit: "Hd.",
    min: 0,
    max: 0.15,
    step: 0.01,
    direction: "ltr",
    title: "Losses after first week",
    toolTip: "Calves lost after their first week",
    formatMinMax: getPercentageFormatter(0),
    format: getPercentageFormatter(1),
    getForwardTransform: (valueModel: HerdValueModel) => (v: number) =>
      v /
      (valueModel.calvesRemainingAfterAbortions -
        valueModel.stillborns -
        valueModel.firstWeekCalfLosses),
    getReverseTransform: (valueModel: HerdValueModel) => (v: number) =>
      Math.round(
        v *
          (valueModel.calvesRemainingAfterAbortions -
            valueModel.stillborns -
            valueModel.firstWeekCalfLosses)
      ),
  },
  calfGrowthRateKgPerDay: {
    unit: "KG",
    min: 0.5,
    max: 2,
    step: 0.1,
    numericInputStep: 0.01,
    direction: "ltr",
    title: "Calf growth rate (kg/day)",
    forwardTransform: (n) => n,
    reverseTransform: (n) => n,
  },
};

export const BestHerdScenario: Readonly<HerdScenario> = {
  cowMortalityFraction: 0.005,
  cowsPregnantFraction: 1,
  cowsWithTwinsFraction: 0.04,
  abortionFraction: 0,
  stillbirthFraction: 0,
  firstWeekLossesFraction: 0,
  lossesAfterFirstWeekFraction: 0,
  calfGrowthRateKgPerDay: 1.5,
};

export const DefaultHerdScenario: Readonly<HerdScenario> = {
  cowMortalityFraction: 0.01,
  cowsPregnantFraction: 0.8,
  cowsWithTwinsFraction: 0.03,
  abortionFraction: 0.02,
  stillbirthFraction: 0.04,
  firstWeekLossesFraction: 0.01,
  lossesAfterFirstWeekFraction: 0.01,
  calfGrowthRateKgPerDay: 1.1,
};

export const WorstHerdScenario: Readonly<HerdScenario> = {
  cowMortalityFraction: 0.03,
  cowsPregnantFraction: 0.7,
  cowsWithTwinsFraction: 0.02,
  abortionFraction: 0.04,
  stillbirthFraction: 0.1,
  firstWeekLossesFraction: 0.1,
  lossesAfterFirstWeekFraction: 0.05,
  calfGrowthRateKgPerDay: 0.5,
};

export const ReferenceHerdScenario: Readonly<HerdScenario> = {
  cowMortalityFraction: 0.02,
  cowsPregnantFraction: 0.75,
  cowsWithTwinsFraction: 0.03,
  abortionFraction: 0.01,
  stillbirthFraction: 0.025,
  firstWeekLossesFraction: 0.02,
  lossesAfterFirstWeekFraction: 0.03,
  calfGrowthRateKgPerDay: 1.1,
};

export const CowClassifications: ParameterClassification[] = [
  {
    id: "1",
    title: "Fertility",
    cardFooter: (
      <span>
        <small>Placeholder text 1. HerdPlan can help you ...</small>
      </span>
    ),
    keys: ["cowsPregnantFraction", "cowsWithTwinsFraction", "abortionFraction"],
  },
  {
    id: "2",
    title: "Mortality",
    cardFooter: (
      <span>
        <small>Placeholder text 2. HerdPlan can help you ...</small>
      </span>
    ),
    keys: [
      "cowMortalityFraction",
      "stillbirthFraction",
      "firstWeekLossesFraction",
      "lossesAfterFirstWeekFraction",
    ],
  },
  {
    id: "3",
    title: "Growth",
    cardFooter: (
      <span>
        <small>Placeholder text 3. HerdPlan can help you...</small>
      </span>
    ),
    keys: ["calfGrowthRateKgPerDay"],
  },
];
